import React from 'react';
import FadeIn from 'legacy/common/components/transition/FadeIn';
import { Formik } from 'formik';
import { v4 as uuid } from 'uuid';
import OfferEligibilitySelection from '../../offers/offer-create/OfferEligibility';
import Kickback from '../../offers/offer-create/Kickback';
import OfferName from '../../offers/offer-create/OfferName';
import CreateOfferActions from '../../offers/offer-create/CreateOfferActions';
import {
  OfferEligibilityType,
  OfferType,
  OfferTypeWithIds
} from 'legacy/events/types';
import { useCreateOffer } from 'legacy/events/hooks';
import { useDispatch } from 'react-redux';
import { showModal } from 'legacy/actions/modalActions';
import * as yup from 'yup';
import AdvancedOfferOptions from '../../offers/offer-create/AdvanceOfferOptions';

interface CreateOfferPageProps {
  eventId: string;
  onClose: () => void;
}

const CreateOfferPage = ({ eventId, onClose }: CreateOfferPageProps) => {
  const validationSchema = yup.object({
    name: yup.string().required('Name is required'),
    rewards: yup.object().shape({
      kickbackRewards: yup.object().shape({
        value: yup.number().min(0.01, 'Value must be greater than 0').required()
      })
    }),
    eligibilityCriteria: yup.array().of(
      yup.object().shape({
        selector: yup.string().required(),
        users: yup.array().when('selector', {
          is: OfferEligibilityType.USERS,
          then: yup.array().min(1, 'Users are required').required(),
          otherwise: yup.array().notRequired()
        })
      })
    )
  });

  const dispatch = useDispatch();

  const initialValues: OfferType = {
    name: '',
    eligibilityCriteria: [
      {
        id: uuid(),
        mustHaveTicket: false,
        selector: 'users',
        users: []
      }
    ],
    rewards: {
      kickbackRewards: {
        kind: 'percentage',
        value: 0,
        saleConditions: {
          unit: 'ticket',
          tierIds: []
        }
      }
    }
  };

  const createOffer = useCreateOffer();

  const handleCreateOffer = (values: OfferType) => {
    const offer: OfferTypeWithIds = {
      ...values,
      rewards: {
        kickbackRewards: [values.rewards.kickbackRewards]
      },
      eligibilityCriteria: values.eligibilityCriteria.map((eligibility) => ({
        mustHaveTicket: eligibility.mustHaveTicket,
        selector: eligibility.selector,
        ids: eligibility.users.map((user) => user._id)
      }))
    };

    createOffer.mutate(
      {
        eventId,
        body: offer
      },
      {
        onSuccess: () => {
          onClose();
        },
        onError: (error) => {
          dispatch(
            showModal('ALERT', {
              message: error?.message || 'Something went wrong'
            })
          );
        }
      }
    );
  };

  return (
    <>
      <Formik
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={handleCreateOffer}
      >
        {({ handleSubmit }) => (
          <>
            <FadeIn>
              <div className="pt-[36x] md:pt-[90px] -mt-[36px] pb-20 md:w-[630px] md:mx-auto">
                <form onSubmit={handleSubmit}>
                  <div className="flex flex-row mt-4 mb-4 ml-4 mr-4">
                    <div className="w-full flex flex-col">
                      <OfferName />
                      <Kickback />
                      <OfferEligibilitySelection />
                      <AdvancedOfferOptions eventId={eventId} />
                    </div>
                  </div>
                </form>
              </div>
            </FadeIn>
            <CreateOfferActions handleCreateOffer={handleSubmit} />
          </>
        )}
      </Formik>
    </>
  );
};

export default CreateOfferPage;
